import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import {
  LayoutDashboard,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  DollarSign,
  Shield,
  Power,
  Store,
  ShoppingBag,
  Settings,
  Wrench,
} from "lucide-react";
import SalesOnly from "../../Assets/SalesOnly.svg";
import ConfirmLogoutModal from "../Utils/ConfirmLogoutModal";
import LoadingScreen from "../Utils/LoadingScreen";

const iconMap = {
  Dashboard: LayoutDashboard,
  "Super Admin": Shield,
  Sales: DollarSign,
  Outlets: Store,
  "Restaurant Reports": ShoppingBag,
  Services: Wrench,
};

const getAdminIdFromSession = () => {
  try {
    const stored = sessionStorage.getItem("userData");
    if (!stored) {
      return null;
    }
    const userData = JSON.parse(stored);
    const adminId = userData?.adminMeta?.adminId;
    return adminId ? String(adminId) : null;
  } catch (error) {
    console.error("Error parsing userData from sessionStorage:", error);
    return null;
  }
};

export default function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  onSubItemClick,
  onAuthorizationChange,
}) {
  const { api } = useContext(GlobalContext);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedSubItem, setSelectedSubItem] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const fetchCalledRef = useRef(false);
  const retryTimeoutRef = useRef(null);

  useEffect(() => {
    if (fetchCalledRef.current) {
      return;
    }
    fetchCalledRef.current = true;
    const controller = new AbortController();

    const fetchMenuAccess = async () => {
      try {
        setLoading(true);
        if (onAuthorizationChange) {
          onAuthorizationChange(null);
        }

        let adminId = getAdminIdFromSession();

        if (!adminId) {
          retryTimeoutRef.current = setTimeout(() => {
            if (!controller.signal.aborted) {
              const retryAdminId = getAdminIdFromSession();
              if (retryAdminId) {
                fetchCalledRef.current = false;
                fetchMenuAccess();
              } else {
                setError("unauthorized");
                setMenuItems([]);
                setIsAuthorized(false);
                setLoading(false);
                if (onAuthorizationChange) {
                  onAuthorizationChange(false);
                }
              }
            }
          }, 1000);
          return;
        }

        const payload = { adminId };
        const response = await api.post("Admin/v1.0/getUserAccess", payload);

        if (
          response.data.responseCode === "00" &&
          response.data.response?.length > 0
        ) {
          const transformedMenu = response.data.response.map((item) => ({
            name: item.menu_title,
            icon: iconMap[item.menu_title] || LayoutDashboard,
            path: item.submenus.length === 0 ? item.menu_url : null,
            subItems:
              item.submenus.length > 0
                ? item.submenus.map((sub) => ({
                    name: sub.submenu_title,
                    path: sub.submenu_url,
                  }))
                : null,
          }));

          setMenuItems(transformedMenu);
          setIsAuthorized(true);
          setError(null);
          if (onAuthorizationChange) {
            onAuthorizationChange(true);
          }
        } else {
          setMenuItems([]);
          setIsAuthorized(false);
          setError("unauthorized");
          if (onAuthorizationChange) {
            onAuthorizationChange(false);
          }
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching menu access:", error);
          setMenuItems([]);
          setIsAuthorized(false);
          setError("unauthorized");
          if (onAuthorizationChange) {
            onAuthorizationChange(false);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMenuAccess();

    return () => {
      controller.abort();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [api, onAuthorizationChange]);

  useEffect(() => {
    if (menuItems.length === 0) return;

    menuItems.forEach((item) => {
      if (item.subItems?.some((sub) => sub.path === location.pathname)) {
        setOpenDropdown(item.name);
        const foundSubItem = item.subItems.find(
          (sub) => sub.path === location.pathname
        );
        setSelectedSubItem(foundSubItem);
      } else if (item.path === location.pathname) {
        setSelectedSubItem(item);
      }
    });
  }, [location.pathname, menuItems]);

  const toggleDropdown = useCallback(
    (item) => {
      if (item.subItems) {
        setOpenDropdown((prev) => (prev === item.name ? null : item.name));
      }
      if (!sidebarOpen) {
        setSidebarOpen(true);
      } else if (item.path) {
        navigate(item.path);
        setSelectedSubItem(item);
        if (onSubItemClick) onSubItemClick(item);
      }
    },
    [sidebarOpen, setSidebarOpen, navigate, onSubItemClick]
  );

  const handleSubItemClick = useCallback(
    (sub) => {
      setSelectedSubItem(sub);
      navigate(sub.path);
      if (onSubItemClick) onSubItemClick(sub);
    },
    [navigate, onSubItemClick]
  );

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem("userData");
    localStorage.clear();
    navigate("/login", { replace: true });
  }, [navigate]);

  return (
    <aside
      className={`fixed top-0 left-0 bg-white dark:bg-gray-900 shadow-lg h-screen transition-all duration-300 z-50 ${
        sidebarOpen ? "w-60" : "w-16"
      }`}
    >
      <div className="flex flex-col h-full">
        <div
          className={`flex justify-center items-center border-b border-gray-200 transition-all duration-300 ${
            sidebarOpen ? "h-16 px-4" : "h-16 px-2"
          }`}
        >
          <div className="flex items-center gap-3">
            {!sidebarOpen && (
              <div
                onClick={() => setSidebarOpen(true)}
                className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center cursor-pointer hover:bg-blue-600 transition-colors"
              >
                <span className="text-white font-bold text-lg">B</span>
              </div>
            )}
            {sidebarOpen && (
              <>
                <img
                  src={SalesOnly}
                  alt="Bumppy"
                  style={{ height: "40px", width: "160px" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    const fallback = e.target.nextElementSibling;
                    if (fallback) fallback.style.display = "flex";
                  }}
                />
                <div
                  className="w-8 h-8 bg-blue-500 rounded-lg items-center justify-center"
                  style={{ display: "none" }}
                >
                  <span className="text-white font-bold text-lg">S</span>
                </div>
              </>
            )}
            {sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 rounded-full hover:bg-gray-200 transition-colors shadow border"
                aria-label="Collapse sidebar"
              >
                <ChevronLeft size={18} />
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <LoadingScreen />
          </div>
        )}

        {/* Menu Items */}
        {!loading && (
          <div className="flex-1 overflow-y-auto py-2">
            {error ? (
              <div className="p-4 text-center">
                {sidebarOpen && (
                  <p className="text-red-500 text-sm">Access Denied</p>
                )}
              </div>
            ) : menuItems.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No menu items available
              </div>
            ) : (
              <div className="space-y-1">
                {menuItems.map((item, index) => {
                  const Icon = item.icon;
                  const isOpen = openDropdown === item.name;
                  const isSelected =
                    selectedSubItem?.name === item.name ||
                    location.pathname === item.path;

                  return (
                    <div key={`${item.name}-${index}`} className="px-3">
                      <div
                        onClick={() => toggleDropdown(item)}
                        className={`flex items-center justify-between gap-3 p-1 cursor-pointer rounded-lg transition-all duration-200 group ${
                          isOpen || isSelected
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`p-2 rounded-lg transition-all duration-200 ${
                              isOpen || isSelected
                                ? "bg-blue-500 text-white"
                                : "bg-gray-100 text-gray-600 group-hover:bg-blue-500 group-hover:text-white"
                            }`}
                          >
                            <Icon size={18} />
                          </div>
                          {sidebarOpen && (
                            <span className="font-medium text-sm">
                              {item.name}
                            </span>
                          )}
                        </div>
                        {sidebarOpen && item.subItems && (
                          <div className="transition-transform duration-200">
                            {isOpen ? (
                              <ChevronUp size={16} className="text-blue-500" />
                            ) : (
                              <ChevronDown
                                size={16}
                                className="text-gray-400"
                              />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Sub Items */}
                      {isOpen && sidebarOpen && item.subItems && (
                        <div className="ml-8 flex flex-col text-gray-600 mt-1">
                          {item.subItems.map((sub, subIndex) => {
                            const isSubSelected =
                              selectedSubItem?.name === sub.name ||
                              location.pathname === sub.path;

                            return (
                              <span
                                key={`${sub.name}-${subIndex}`}
                                onClick={() => handleSubItemClick(sub)}
                                className={`p-1 text-sm cursor-pointer rounded transition-all me-3 flex items-center ${
                                  isSubSelected
                                    ? "text-blue-600 font-semibold"
                                    : "hover:text-blue-600"
                                }`}
                              >
                                <span className="mr-2 text-xl before:content-['•']"></span>
                                {sub.name}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Logout Button - only if authorized */}
        {isAuthorized && (
          <ConfirmLogoutModal
            onConfirm={handleLogout}
            trigger={
              <div className="flex items-center mx-3 space-x-2 mb-4 cursor-pointer hover:bg-red-400 transition-colors p-1 rounded text-gray-800 group">
                <div className="p-2 rounded bg-gray-100 flex items-center justify-center transition-colors group-hover:bg-red-500">
                  <Power
                    size={20}
                    className="text-gray-500 transition-colors group-hover:text-white"
                  />
                </div>
                {sidebarOpen && (
                  <span className="transition-colors group-hover:text-white">
                    Logout
                  </span>
                )}
              </div>
            }
          />
        )}
      </div>
    </aside>
  );
}
